import { Base } from './base'
import { Global } from '@/models/global'
import Joi from 'joi'

const UPLOAD = {
  TCP: {
    name: 'TCP 协议',
    module: 'data-upload-tcp',
    parameters: [
      {
        required: true,
        key: 'host',
        label: '地址',
        type: 'string'
      },
      {
        required: true,
        key: 'port',
        label: '端口号',
        type: 'number'
      }
    ]
  },
  HTTP: {
    name: 'HTTP 协议',
    module: 'data-upload-http',
    parameters: [
      {
        required: true,
        key: 'uri',
        label: 'URI',
        type: 'string',
        prefix: 'http://'
      },
      {
        required: false,
        key: 'tokenHeader',
        label: 'Token Header',
        type: 'string'
      },
      {
        required: false,
        key: 'token',
        label: 'Token',
        type: 'string'
      }
    ]
  },
  MQTT: {
    name: 'MQTT 协议',
    module: 'data-upload-mqtt',
    parameters: [
      {
        required: true,
        key: 'protocol',
        label: '协议类型',
        type: 'select',
        options: [ 'mqtt', 'mqtts' ]
      },
      {
        required: true,
        key: 'host',
        label: '地址',
        type: 'string'
      },
      {
        required: false,
        key: 'port',
        label: '端口号',
        type: 'number'
      },
      {
        required: false,
        key: 'clientId',
        label: '客户端 ID',
        type: 'string'
      },
      {
        required: false,
        key: 'username',
        label: '用户名',
        type: 'string'
      },
      {
        required: false,
        key: 'password',
        label: '密码',
        type: 'password'
      },
      {
        required: true,
        key: 'topic',
        label: '消息发布主题',
        type: 'string'
      }
    ]
  },
  AZURE: {
    name: 'Azure IoTHub SDK',
    module: 'data-upload-azure',
    parameters: [
      {
        required: true,
        key: 'connectionString',
        label: '连接字符串',
        type: 'string'
      }
    ]
  },
  AWS: {
    name: 'AWS IoT SDK',
    module: 'data-upload-aws',
    parameters: [
      {
        required: true,
        key: 'keyPath',
        label: '设备私钥路径',
        type: 'string',
        default: '/ruff/app/keys/',
        invalidFeedback: '在应用配置中上传文件'
      },
      {
        required: true,
        key: 'certPath',
        label: '设备证书路径',
        type: 'string',
        default: '/ruff/app/keys/',
        invalidFeedback: '在应用配置中上传文件'
      },
      {
        required: true,
        key: 'caPath',
        label: '根证书路径',
        type: 'string',
        default: '/ruff/app/keys/',
        invalidFeedback: '在应用配置中上传文件'
      },
      {
        required: true,
        key: 'host',
        label: '地址',
        type: 'string'
      },
      {
        required: true,
        key: 'clientId',
        label: '客户端 ID',
        type: 'string'
      },
      {
        required: true,
        key: 'topic',
        label: '消息发布主题',
        type: 'string'
      }
    ]
  },
  ALIYUN: {
    name: 'Aliyun IoT SDK',
    module: 'data-upload-aliyun',
    parameters: [
      {
        required: true,
        key: 'ProductKey',
        label: '产品密钥',
        type: 'string'
      },
      {
        required: true,
        key: 'DeviceName',
        label: '设备名称',
        type: 'string'
      },
      {
        required: true,
        key: 'DeviceSecret',
        label: '设备密钥',
        type: 'string'
      },
      {
        required: true,
        key: 'protocol',
        label: '协议类型',
        type: 'select',
        options: [ 'mqtt', 'mqtts' ]
      },
      {
        required: false,
        key: 'caPath',
        label: '根证书路径',
        type: 'string',
        default: '/ruff/app/keys/',
        placeholder: '当协议类型为mqtts时必需填写',
        invalidFeedback: '在应用配置中上传文件'
      },
      {
        required: true,
        key: 'topic',
        label: '消息发布主题',
        type: 'string'
      }
    ]
  }
}

export class GlobalManager extends Base {
  constructor (vueComponent) {
    super(vueComponent)

    this._global = new Global(vueComponent)
    this._operationMap = {
      basic: {
        click: this.editBasicConfig
      },
      upload: {
        click: this.editUploadConfig
      }
    }

    this._addListeners()
  }

  async genTreeData () {
    return [
      {
        name: '基本配置',
        operations: [ 'click' ],
        class: 'basic'
      },
      {
        name: '上传配置',
        operations: [ 'click' ],
        class: 'upload'
      }
    ]
  }

  async processOperation (operation, data) {
    let cb = this._operationMap[data.class][operation]

    await cb.call(this, data)
  }

  async editBasicConfig () {
    let data = await this._global.get()
    let metadata = data ? data.metadata : {}

    data = await this._requestForm('global', {
      name: data ? data.name : '',
      version: data ? data.version : '',
      serialNumber: data ? data.metadata.gSN : ''
    })

    await this._global.update({
      name: data.name,
      version: data.version
    })

    await this._global.update({
      metadata: Object.assign(metadata, {
        gSN: data.serialNumber
      })
    })

    this.alertSuccess('保存成功')
  }

  async editUploadConfig () {
    let upload = (await this._global.get()).upload
    let data = {
      uploadOptions: UPLOAD,
      args: upload.args,
      type: upload.type
    }

    return this._requestForm('upload', data)
  }

  async inspectUploadConfig () {
    let upload = (await this._global.get()).upload
    let data = {
      uploadOptions: UPLOAD,
      args: upload.args,
      type: upload.type
    }

    this._requestForm('upload', data, false)
  }

  get _uploadConfigSchemaMap () {
    return {
      TCP: {
        host: Joi.string().required(),
        port: Joi.number().required()
      },
      HTTP: {
        uri: Joi.string().required(),
        token: Joi.string(),
        tokenHeader: Joi.string()
      },
      MQTT: {
        protocol: Joi.string().required(),
        host: Joi.string().required(),
        port: Joi.number(),
        clientId: Joi.string(),
        username: Joi.string(),
        password: Joi.string(),
        topic: Joi.string().required()
      },
      AZURE: {
        connectionString: Joi.string().required()
      },
      AWS: {
        keyPath: Joi.string().required(),
        certPath: Joi.string().required(),
        caPath: Joi.string().required(),
        host: Joi.string().required(),
        clientId: Joi.string().required(),
        topic: Joi.string().required()
      },
      ALIYUN: {
        ProductKey: Joi.string().required(),
        DeviceName: Joi.string().required(),
        DeviceSecret: Joi.string().required(),
        protocol: Joi.string().required(),
        caPath: Joi.string(),
        topic: Joi.string().required()
      }
    }
  }

  _verifyUploadConfig (data) {
    const res = Joi.validate(data, {
      type: Joi.string().required(),
      args: Joi.object().required()
    })

    if (res.error) {
      throw res.error
    }
    let schema = this._uploadConfigSchemaMap[res.value.type]
    if (res.value.type === 'ALIYUN' && res.value.args.protocol === 'mqtts') {
      schema.caPath = Joi.string().required()
    }
    if (res.value.type === 'HTTP' && (res.value.args.token || res.value.args.tokenHeader)) {
      schema.token = Joi.string().required()
      schema.tokenHeader = Joi.string().required()
    }
    if (!schema) {
      throw new Error(`Invalid type: '${res.value.type}'`)
    }

    const argsRes = Joi.validate(data.args, schema)
    if (argsRes.error) {
      throw argsRes.error
    }

    res.value.args = argsRes.value

    return res.value
  }

  async _addListeners () {
    /* edit upload-config */
    this._addFormListener('upload', async data => {
      const value = this._verifyUploadConfig(data)

      await this._global.update({
        upload: {
          type: value.type,
          module: UPLOAD[value.type].module,
          args: value.args
        }
      })

      this.alertSuccess('保存成功')
    })
  }
}



// WEBPACK FOOTER //
// ./src/components/home/controllers/global.js